type FilePathSegment = {
  text: string
  href?: string
}

const fileExtension =
  "md|markdown|html?|xlsx?|csv|pdf|txt|json|jsonl|png|jpe?g|gif|webp|svg|docx?|pptx?|zip|ts|tsx|js|jsx|mjs|cjs|css|scss|py|sh|sql|xml|ya?ml"
const fileUrlSource = `file://[^\\s<>"'\`]*?\\.(?:${fileExtension})(?:[?#][^\\s<>"'\`),.;!?]*)?`
const absolutePathSource = `(?:/|[A-Za-z]:[\\\\/]|\\\\\\\\)[^\\n<>"'\`]*?\\.(?:${fileExtension})`
const filePathPattern = new RegExp(
  `(^|[\\s([{<])((?:${fileUrlSource}|${absolutePathSource}))([),.;!?]*)(?=$|[\\s)\\]}>])`,
  "gi",
)

function encodeFilePath(filepath: string) {
  const normalized = filepath.replace(/\\/g, "/")
  const absolute = /^[A-Za-z]:/.test(normalized) ? `/${normalized}` : normalized
  return absolute
    .split("/")
    .map((segment, index) => (index === 1 && /^[A-Za-z]:$/.test(segment) ? segment : encodeURIComponent(segment)))
    .join("/")
}

export function filePathHref(filepath: string) {
  const value = filepath.trim()
  if (!value) return
  if (value.startsWith("file://")) return value
  if (!/^(?:\/|[A-Za-z]:[\\/]|\\\\)/.test(value)) return
  return `file://${encodeFilePath(value)}`
}

export function splitFilePathLinks(text: string): FilePathSegment[] {
  const segments: FilePathSegment[] = []
  let cursor = 0

  for (const match of text.matchAll(filePathPattern)) {
    const prefix = match[1] ?? ""
    const filepath = match[2] ?? ""
    const punctuation = match[3] ?? ""
    const href = filePathHref(filepath)
    if (!href) continue

    const start = match.index + prefix.length
    if (start > cursor) segments.push({ text: text.slice(cursor, start) })
    segments.push({ text: filepath, href })
    if (punctuation) segments.push({ text: punctuation })
    cursor = start + filepath.length + punctuation.length
  }

  if (cursor < text.length) segments.push({ text: text.slice(cursor) })
  return segments.length ? segments : [{ text }]
}

export function linkFilePathTextNodes(root: HTMLElement) {
  const nodeFilter = root.ownerDocument.defaultView?.NodeFilter ?? NodeFilter
  const walker = root.ownerDocument.createTreeWalker(root, nodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.textContent?.trim()) return nodeFilter.FILTER_REJECT
      const parent = node.parentElement
      if (!parent) return nodeFilter.FILTER_REJECT
      if (parent.closest("a, pre, code, kbd, button")) return nodeFilter.FILTER_REJECT
      return nodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []
  let current = walker.nextNode()
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) nodes.push(current as Text)
    current = walker.nextNode()
  }

  for (const node of nodes) {
    const segments = splitFilePathLinks(node.textContent ?? "")
    if (!segments.some((segment) => segment.href)) continue
    const fragment = root.ownerDocument.createDocumentFragment()
    for (const segment of segments) {
      if (!segment.href) {
        fragment.append(root.ownerDocument.createTextNode(segment.text))
        continue
      }
      const link = root.ownerDocument.createElement("a")
      link.href = segment.href
      link.className = "external-link"
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.textContent = segment.text
      fragment.append(link)
    }
    node.replaceWith(fragment)
  }
}
